import { useMutation } from '@tanstack/react-query'
import { supabase } from '@/infrastructure/supabase/client'
import { useEditorStore } from '@/store/editorStore'
import type { BlockType, BlockProps, BlockSettings } from '@brix/types'

const SUPABASE_URL = import.meta.env['VITE_SUPABASE_URL'] as string

export function useGenerateAiPage() {
  return useMutation({
    mutationFn: async (opts: { siteId: string; prompt: string }) => {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${SUPABASE_URL}/functions/v1/ai-generate-page`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(opts),
      })
      const body = await res.json() as { pageId?: string; blocksCount?: number; error?: string }
      if (!res.ok) throw new Error(body.error ?? `Error ${res.status}`)
      return body as { pageId: string; blocksCount: number }
    },
  })
}

export interface AiChatMessage {
  role: 'user' | 'assistant'
  content: string
}

interface AiEditOperation {
  op: 'update' | 'add' | 'remove' | 'reorder'
  blockId?: string
  props?: Record<string, unknown>
  settings?: Record<string, unknown>
  afterBlockId?: string
  block?: { type: BlockType; props?: Record<string, unknown> }
  blockIds?: string[]
}

// Applies each proposed operation through the same store actions a human
// editing by hand would trigger — undo/redo and the normal isDirty -> Cmd+S
// -> saveBlocks() path all come for free. Unknown blockIds are no-ops
// (updateBlockProps/updateBlockSettings/deleteBlock already skip ids that
// aren't found) rather than errors — a stale reference shouldn't crash the chat.
function applyAiOperations(operations: AiEditOperation[]) {
  const store = useEditorStore.getState()
  for (const op of operations) {
    switch (op.op) {
      case 'update':
        if (!op.blockId) break
        if (op.props) store.updateBlockProps(op.blockId, op.props as Partial<BlockProps>)
        if (op.settings) store.updateBlockSettings(op.blockId, op.settings as Partial<BlockSettings>)
        break
      case 'add':
        if (!op.block?.type) break
        store.addBlock(op.block.type, (op.block.props ?? {}) as Partial<BlockProps>, op.afterBlockId || undefined)
        break
      case 'remove':
        if (!op.blockId) break
        store.deleteBlock(op.blockId)
        break
      case 'reorder':
        if (!op.blockIds?.length) break
        store.setBlockOrder(op.blockIds)
        break
      default:
        console.warn('useAiAssistantChat: unknown operation', op)
    }
  }
}

export function useAiAssistantChat() {
  return useMutation({
    mutationFn: async (opts: {
      siteId: string
      pageId: string
      message: string
      context: unknown[]
      history: AiChatMessage[]
    }) => {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${SUPABASE_URL}/functions/v1/ai-assistant`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(opts),
      })
      const body = await res.json() as { reply?: string; operations?: AiEditOperation[]; error?: string; retryAfter?: number }
      if (!res.ok) {
        if (res.status === 429) throw new Error('Daily AI assistant limit reached — try again tomorrow.')
        throw new Error(body.error ?? `Error ${res.status}`)
      }
      return body as { reply: string; operations: AiEditOperation[] }
    },
    onSuccess: (data) => {
      applyAiOperations(data.operations ?? [])
    },
  })
}
